import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useMySalarie } from "@/lib/data/salarie";
import { useMyScopes } from "@/lib/data/drh";
import {
  useResolvedBrandingForSalarie,
  useResolvedBrandingForSociete,
  type ResolvedBranding,
} from "@/lib/data/branding";
import { useBranding } from "./BrandingProvider";
import type { BrandingTokens, HslTriplet } from "./tokens";

// =============================================================================
// BrandingSync — applique le branding du tenant selon l'espace courant
// -----------------------------------------------------------------------------
// Monté une seule fois sous le Router (pour lire l'URL). Le cobranding tenant
// ne s'applique QUE dans les espaces salarié et DRH ; les espaces Aon (interne)
// et Admin conservent toujours le branding Aon par défaut.
//
// - Espace salarié (`/app`, hors /drh /aon /admin) : branding du tenant du
//   salarié connecté (cascade op > site > BU > filiale > société > groupe).
// - Espace DRH (`/app/drh/*`) : branding de la société du périmètre DRH
//   (cascade société > groupe).
// - Espace Aon (`/app/aon/*`) & Admin (`/app/admin/*`) & pages publiques :
//   branding Aon par défaut.
//
// Ne rend rien.
// =============================================================================

type BrandingSpace = "salarie" | "drh" | "aon" | "admin" | "public";

/** Déduit l'espace courant à partir du chemin d'URL. */
function brandingSpaceFromPath(pathname: string): BrandingSpace {
  if (!pathname.startsWith("/app")) return "public";
  const rest = pathname.slice("/app".length);
  if (rest.startsWith("/admin")) return "admin";
  if (rest.startsWith("/aon")) return "aon";
  if (rest.startsWith("/drh")) return "drh";
  return "salarie";
}

const HSL_TRIPLET_RE = /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/;

/** Convertit le sous-ensemble snake_case renvoyé par la RPC en tokens runtime. */
function resolvedToTokens(b: ResolvedBranding): Partial<BrandingTokens> {
  const tokens: Partial<BrandingTokens> = {};

  const colorMap: Array<[keyof ResolvedBranding, keyof BrandingTokens]> = [
    ["brand", "brand"],
    ["brand_foreground", "brandForeground"],
    ["brand_hover", "brandHover"],
    ["brand_muted", "brandMuted"],
    ["accent", "accent"],
    ["accent_foreground", "accentForeground"],
  ];
  for (const [src, dest] of colorMap) {
    const v = b[src];
    if (typeof v === "string" && HSL_TRIPLET_RE.test(v.trim())) {
      (tokens[dest] as HslTriplet) = v.trim() as HslTriplet;
    }
  }

  if (b.product_name) tokens.productName = b.product_name;
  if (b.product_tagline) tokens.productTagline = b.product_tagline;
  if (b.logo_url) tokens.logoUrl = b.logo_url;
  if (b.logo_dark_url) tokens.logoDarkUrl = b.logo_dark_url;
  if (b.favicon_url) tokens.faviconUrl = b.favicon_url;
  if (b.font_sans) tokens.fontSans = b.font_sans;
  if (b.font_display) tokens.fontDisplay = b.font_display;
  if (b.footer_legal) tokens.footerLegal = b.footer_legal;

  return tokens;
}

export function BrandingSync() {
  const { pathname } = useLocation();
  const space = brandingSpaceFromPath(pathname);
  const brandingEnabled = space === "salarie" || space === "drh";

  const { setTokens, reset } = useBranding();

  // Source salarié : utilisée dans l'espace salarié (impersonation-safe via
  // effectiveUserId dans useMySalarie).
  const { data: salarie } = useMySalarie();
  const salarieId = salarie?.id ?? null;
  const { data: salarieBranding } = useResolvedBrandingForSalarie(
    brandingEnabled ? salarieId : null,
  );

  // Source DRH : première société du périmètre. Utilisée si pas de fiche salarié
  // (cas DRH pur) — typiquement dans l'espace DRH.
  const { data: scopes } = useMyScopes();
  const drhSocieteId = useMemo(
    () => scopes?.find((s) => s.societe_id)?.societe_id ?? null,
    [scopes],
  );
  const { data: societeBranding } = useResolvedBrandingForSociete(
    brandingEnabled && !salarieId ? drhSocieteId : null,
  );

  const resolved = salarieId ? salarieBranding : societeBranding;

  useEffect(() => {
    // Espaces Aon / Admin / public → branding Aon par défaut.
    if (!brandingEnabled) {
      reset();
      return;
    }
    // En attente du résultat de résolution.
    if (!resolved) return;
    const tokens = resolvedToTokens(resolved);
    if (Object.keys(tokens).length === 0) {
      reset();
    } else {
      setTokens(tokens);
    }
  }, [brandingEnabled, resolved, setTokens, reset]);

  return null;
}
